import { useState, useRef, useEffect, useMemo } from "react";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { CardQuestion, CardActions, PressEnterHint } from "./CardPrimitives";
import { CountrySelect } from "@/components/form/CountrySelect";
import { useIntakeMobileChromeRegistration } from "../IntakeMobileChromeContext";
import {
  getPhonePlaceholder,
  parsePrefilledPhone,
  sanitizeNationalInput,
} from "@/lib/phoneNumber";
import type { CardDef } from "../types";

interface ContactInfoCardProps {
  card: CardDef;
  countryValue: string;
  phoneValue: string;
  emailValue: string;
  onCountryValue: (v: string) => void;
  onPhoneValue: (v: string) => void;
  onPhonePaste?: (country: string, national: string) => void;
  onEmailValue: (v: string) => void;
  onNext: () => void;
  onBack: () => void;
  index: number;
  countryError?: string;
  phoneError?: string;
  emailError?: string;
  loading?: boolean;
  /** Rendered directly beneath the phone field (carrier-compliance consents). */
  phoneExtra?: React.ReactNode;
}

export function ContactInfoCard({
  card,
  countryValue,
  phoneValue,
  emailValue,
  onCountryValue,
  onPhoneValue,
  onPhonePaste,
  onEmailValue,
  onNext,
  onBack,
  index,
  countryError,
  phoneError,
  emailError,
  loading,
  phoneExtra,
}: ContactInfoCardProps) {
  const { _ } = useLingui();
  const [touched, setTouched] = useState(false);
  const phoneInputRef = useRef<HTMLInputElement>(null);

  const phonePlaceholder = useMemo(
    () => getPhonePlaceholder(countryValue) ?? _(msg`555-123-4567`),
    [countryValue, _],
  );

  useEffect(() => {
    phoneInputRef.current?.focus({ preventScroll: true });
  }, [card.id]);

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTouched(true);
      onNext();
    }
  };

  const handlePhonePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text");
    if (!pasted?.trim()) return;

    const parsed = parsePrefilledPhone(pasted, countryValue || "US");
    if (!parsed) return;

    e.preventDefault();
    if (onPhonePaste) {
      onPhonePaste(parsed.country, parsed.national);
    } else {
      onCountryValue(parsed.country);
      onPhoneValue(parsed.national);
    }
  };

  const showPhoneError = touched && phoneError;
  const showCountryError = touched && countryError;
  const showEmailError = touched && emailError;

  useIntakeMobileChromeRegistration(
    () => ({
      onContinue: () => {
        setTouched(true);
        onNext();
      },
      onCancel: onBack,
      continueLoading: loading,
      continueLabel: loading ? _(msg`Preparing…`) : undefined,
    }),
    [_, onNext, onBack, loading],
  );

  return (
    <div>
      <CardQuestion question={card.question} hint={card.hint} index={index} />

      <div className="flex flex-col gap-6 mt-6 max-w-[500px]">
        {/* Phone Input Group */}
        <div>
          <label className="block text-[13px] font-medium text-gray-500 mb-1.5 ml-1 uppercase tracking-wider">
            {_(msg`Mobile number`)}
          </label>
          <div className="flex items-start gap-3 relative">
            <div className="w-[104px] flex-shrink-0 relative">
              <CountrySelect
                mode="dialCode"
                value={countryValue}
                onValueChange={onCountryValue}
                placeholder={_(msg`Code`)}
                buttonClassName={`h-[52px] w-full bg-white/80 backdrop-blur-sm border-[1px] rounded-xl px-4 text-[16px] text-gray-900 border-gray-300 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.03)] focus:outline-none focus:ring-0 focus:border-[#166B93] ${
                  showCountryError ? "!border-red-400" : "hover:border-gray-400"
                }`}
              />
              {showCountryError && (
                <div className="mt-2 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm font-medium text-red-500">{countryError ?? _(msg`Required`)}</p>
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1 w-full relative group">
              <input
                ref={phoneInputRef}
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={15}
                value={phoneValue}
                onChange={(e) => {
                  onPhoneValue(sanitizeNationalInput(e.target.value));
                }}
                onPaste={handlePhonePaste}
                onKeyDown={handleKey}
                placeholder={phonePlaceholder}
                autoComplete="tel-national"
                aria-invalid={showPhoneError ? true : undefined}
                className={`w-full h-[52px] bg-white/80 backdrop-blur-sm border-[1px] rounded-xl px-5 text-[17px] text-gray-900 placeholder-gray-400 border-gray-300 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.03)] focus:outline-none focus:ring-0 focus:border-[#166B93] focus:shadow-[0_4px_20px_rgba(22,107,147,0.08)] disabled:opacity-60 disabled:cursor-not-allowed ${
                  showPhoneError
                    ? "!border-red-400 focus:!border-red-500"
                    : "hover:border-gray-400"
                }`}
              />
              {showPhoneError && (
                <div className="mt-2 flex items-center gap-1.5">
                  <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm font-medium text-red-500">{phoneError}</p>
                </div>
              )}
            </div>
          </div>
          <p className="mt-2 ml-1 text-xs text-gray-500">
            {_(msg`Select your country code, then enter your mobile number.`)}
          </p>
          {phoneExtra}
        </div>

        {/* Email Input */}
        <div className="relative">
          <label className="block text-[13px] font-medium text-gray-500 mb-1.5 ml-1 uppercase tracking-wider">
            {_(msg`Email address`)}
          </label>
          <input
            type="email"
            value={emailValue}
            onChange={(e) => {
              onEmailValue(e.target.value);
            }}
            onKeyDown={handleKey}
            placeholder={_(msg`you@example.com`)}
            autoComplete="email"
            autoCapitalize="none"
            spellCheck="false"
            className={`w-full h-[52px] bg-white/80 backdrop-blur-sm border-[1px] rounded-xl px-5 text-[17px] text-gray-900 placeholder-gray-400 border-gray-300 transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.02)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.03)] focus:outline-none focus:ring-0 focus:border-[#166B93] focus:shadow-[0_4px_20px_rgba(22,107,147,0.08)] disabled:opacity-60 disabled:cursor-not-allowed ${
              showEmailError
                ? "!border-red-400 focus:!border-red-500"
                : "hover:border-gray-400"
            }`}
          />
          {showEmailError && (
            <div className="mt-2 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm font-medium text-red-500">{emailError}</p>
            </div>
          )}
        </div>
      </div>

      <CardActions
        onNext={() => { setTouched(true); onNext(); }}
        onBack={onBack}
        nextLabel={loading ? _(msg`Preparing…`) : undefined}
      />
      {!loading && <PressEnterHint />}
    </div>
  );
}
